import React from "react"

interface TagInputProps {
  id: string
  value: string
  tags: string[]
  placeholder: string
  prefix?: string
  prefixOptions?: { value: string; label: string; default?: boolean }[]
  selectedPrefix?: string
  onChange: (value: string) => void
  onPrefixChange?: (prefix: string) => void
  onRemoveTag: (index: number) => void
  onAddTag?: () => void
  maxItems?: number
}

// Common input classes to prevent Chrome autofill styling
const inputBaseClasses =
  "bg-white text-gray-900 outline-none [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_white] [&:-webkit-autofill]:[-webkit-text-fill-color:#111827]"

/**
 * Tag input component with support for static or selectable prefixes
 * Displays input on top and tags below
 */
const TagInput: React.FC<TagInputProps> = ({
  id,
  value,
  tags,
  placeholder,
  prefix,
  prefixOptions,
  selectedPrefix,
  onChange,
  onPrefixChange,
  onRemoveTag,
  onAddTag,
  maxItems,
}) => {
  const hasStaticPrefix = !!prefix
  const hasPrefixDropdown = prefixOptions && prefixOptions.length > 0
  const hasValue = value.trim().length > 0
  const isAtCapacity = maxItems !== undefined && tags.length >= maxItems

  const currentPrefix =
    selectedPrefix ||
    prefixOptions?.find(opt => opt.default)?.value ||
    prefixOptions?.[0]?.value ||
    ""

  const handleAddClick = () => {
    if (hasValue && onAddTag) {
      onAddTag()
    }
  }

  const AddButton = () => (
    <button
      type="button"
      onClick={handleAddClick}
      disabled={!hasValue}
      className="px-4 py-3 text-sm font-medium rounded-lg cursor-pointer transition-colors bg-brand text-white hover:opacity-90 border-0"
    >
      Add
    </button>
  )

  const renderInput = () => {
    if (hasStaticPrefix) {
      return (
        <div className="flex w-full gap-2">
          <div className="flex flex-1 rounded-lg">
            <span className="inline-flex items-center px-3 text-sm text-gray-500 bg-white font-mono rounded-l-lg border-0 outline-none">
              {prefix}
            </span>
            <input
              id={id}
              type="text"
              value={value}
              onChange={e => onChange(e.target.value)}
              className={`flex-1 p-3 rounded-r-lg border-l border-l-gray-300 ${inputBaseClasses}`}
              placeholder={placeholder}
            />
          </div>
          <AddButton />
        </div>
      )
    }

    if (hasPrefixDropdown) {
      return (
        <div className="flex w-full gap-2">
          <div className="flex flex-1 rounded-lg">
            <select
              value={currentPrefix}
              onChange={e => onPrefixChange?.(e.target.value)}
              className="inline-flex w-[90px] items-center px-3 text-sm text-gray-700 bg-white rounded-l-lg font-mono cursor-pointer border-0 outline-none"
            >
              {prefixOptions?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <input
              id={id}
              type="text"
              value={value}
              onChange={e => onChange(e.target.value)}
              className={`flex-1 p-3 rounded-r-lg border-l border-l-gray-300 ${inputBaseClasses}`}
              placeholder={placeholder}
            />
          </div>
          <AddButton />
        </div>
      )
    }

    return (
      <div className="flex w-full gap-2">
        <input
          id={id}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`flex-1 p-3 rounded-lg border-l border-l-gray-300 ${inputBaseClasses}`}
          placeholder={placeholder}
        />
        <AddButton />
      </div>
    )
  }

  let displayPrefix = ""
  if (hasStaticPrefix) {
    displayPrefix = prefix
  } else if (hasPrefixDropdown) {
    displayPrefix = currentPrefix
  }

  return (
    <div className="space-y-2">
      {!isAtCapacity && renderInput()}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-1.5 bg-brand text-white text-sm rounded-lg transition-colors border-0"
            >
              {displayPrefix && (
                <span className="text-white/70 font-mono">{displayPrefix}</span>
              )}
              <span className="max-w-[250px] truncate font-medium">{tag}</span>
              <button
                type="button"
                onClick={() => onRemoveTag(index)}
                className="text-white/70 hover:text-white ml-1.5 transition-colors border-0 outline-none bg-transparent"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default TagInput
